import { BlobServiceClient } from '@azure/storage-blob';
import moment from 'moment';
import { STORAGE } from 'src/constants';
import { isEmpty } from 'src/helpers/check';
import { compressImageIfNeeded, isCompressibleImage } from 'src/utils/imageCompression';

export const createFileName = (fileName, uniqueFileSlug = false) => (`${uniqueFileSlug ? `${uniqueFileSlug}-` : ''}${moment().format('YYYY-MM-DD-HH-mm-ss-SS')}-${fileName.replace(/[^a-z0-9.-]/gi, '')}`);

export const findUniqueFileSlug = (fileName, uniqueFileSlug = false) => {
  if (!uniqueFileSlug) {
    return false;
  }
  return !!fileName.match(new RegExp(`^${uniqueFileSlug}-`));
};

// 307200B = 300KB   1450000B = 1.38MB   1468006B = 1.4MB   1572864B = 1.5MB
// The default was 1450000 until CON-1185 (Nov 2025) tightened it to 300KB.
export const MAX_ATTACHMENT_SIZE_BYTES = 307200;

// CON-1674 — audio only. A 120-second voice memo at 32 kbps is ~470 KB, so it
// cannot live under 300 KB. Measured on 2 Sep 2026: AT&T delivered a 480,608
// byte MP3 byte-for-byte, and T-Mobile and Verizon delivered too. The limit is
// DELIBERATELY NOT RAISED GLOBALLY — that evidence is audio-shaped and says
// nothing about a 900 KB PDF, which no carrier test ever covered.
export const MAX_AUDIO_SIZE_BYTES = 500000;

/* CON-1674 — the ceiling for a voice memo RECORDING, which is a different
 * thing from an audio file attached from disk, and must not share its limit.
 *
 * THE BUG THIS FIXES. Recordings failed at ~31 seconds with "File size must
 * be less than 488Kb". MAX_AUDIO_SIZE_BYTES above was sized from carrier
 * evidence for what goes OUT — 120 s of 32 kbps MP3 is ~470 KB — but it was
 * being applied to what comes IN, and the browser records at roughly four
 * times our output bitrate. Measured on QA, Chrome, four real webm -> mp3
 * pairs (the MP3 is 32 kbps = 4,000 B/s, which gives the duration):
 *
 *     webm 186,734 B   mp3 46,880 B   ~11.7 s   127.5 kbps
 *     webm 248,558 B   mp3 62,288 B   ~15.6 s   127.7 kbps
 *     webm 162,584 B   mp3 40,832 B   ~10.2 s   127.4 kbps
 *     webm 167,414 B   mp3 42,128 B   ~10.5 s   127.2 kbps
 *
 * Chrome's default is ~128 kbps = 16,000 B/s, so 500,000 B is reached at
 * 31.25 s. That is the "roughly 30 seconds" customers reported.
 *
 * WHY THE WIRE LIMIT DOES NOT APPLY HERE. The recording never reaches a
 * carrier. It goes to blob storage, ffmpeg converts it to 32 kbps mono MP3,
 * and THAT is what is sent. The server enforces no carrier ceiling on the MP3
 * either (its guards are 25 MB in / 10 MB out, sanity only) — what keeps a
 * memo under the carrier limit is the 120-second cap combined with the 32
 * kbps output profile: 120 x 4,000 B/s ≈ 470 KB, regardless of what the
 * browser recorded at. So this number is about the intermediate only.
 *
 * SIZING. 120 s at Chrome's 128 kbps is 1.92 MB. Safari records mp4/AAC and
 * its default is unmeasured, so this leaves headroom rather than guessing:
 * 4 MB covers 120 s at anything up to ~270 kbps. It is NOT a wire number and
 * must not be tuned as one. The recorder also meters bytes as it goes and
 * stops before crossing this, so in practice it is never hit; it is the
 * backstop, not the limit.
 *
 * WHY MAX_AUDIO_SIZE_BYTES IS DELIBERATELY LEFT ALONE. An .mp3 attached from
 * disk is sent to the carrier AS-IS at its stored size, so the carrier
 * evidence that produced 500,000 applies to it exactly. Unifying the two
 * ceilings would either break the disk path (too small) or let a disk
 * attachment past the carrier limit (too large). They are different things
 * that happen to share a MIME type. Do not merge them. */
export const MAX_RECORDING_SIZE_BYTES = 4 * 1024 * 1024;

const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'wav', 'ogg', 'oga', 'aac', 'amr', 'webm', 'mp4'];

/**
 * Is this file audio, for the purposes of the size ceiling?
 *
 * MIME first, extension as a fallback: a MediaRecorder blob arrives with
 * 'audio/webm;codecs=opus' and often no useful filename at all, while a file
 * picked from disk may carry an empty type on some browsers. The extension
 * list includes webm and mp4 because those are what Chrome/Firefox and Safari
 * actually record — but only when the MIME says audio, so a real video .mp4
 * still gets the ordinary 300 KB limit.
 */
export const isAudioFile = (file) => {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('audio/')) return true;
  if (type.startsWith('video/')) return false;
  const ext = (file.name || '').toLowerCase().split('.').pop();
  return AUDIO_EXTENSIONS.includes(ext);
};

/**
 * The ceiling that applies to one file. An explicit override always wins —
 * three Join Form call sites pass 1.5MB and must keep it.
 */
export const getMaxFileSizeForFile = (file, override = null) => {
  if (override) return override;
  return isAudioFile(file) ? MAX_AUDIO_SIZE_BYTES : MAX_ATTACHMENT_SIZE_BYTES;
};

/**
 * Human label for a byte limit, so the refusal message can state the limit
 * that was actually applied. Before CON-1674 the message was the hard-coded
 * string 'File size must be less than 300Kb', which was already wrong on the
 * Join Form screens — they accept 1.5MB and said 300Kb on refusal. Adding a
 * second limit would have made that worse in two places instead of one.
 */
export const fileSizeLimitLabel = (bytes) => (
  bytes >= 1000000
    ? `${(bytes / 1048576).toFixed(1)}MB`
    : `${Math.round(bytes / 1024)}Kb`
);

export const fileSizeErrorMessage = (file, override = null) => (
  `File size must be less than ${fileSizeLimitLabel(getMaxFileSizeForFile(file, override))}`
);

export const validateFileSize = (newFileUpload, maxFileSize = null) => {
  const { file } = newFileUpload || {};
  if (!file) return true;
  const limit = getMaxFileSizeForFile(file, maxFileSize);
  return limit > 0 && file.size < limit;
};

/**
 * Validate file size for form submission (Yup validation).
 * Skips size validation for compressible images (jpg, png, gif) since they will be
 * automatically compressed during upload in loadFileToAzureAndGetStatusCode.
 * Non-image files (PDFs, videos, etc.) still require size validation.
 */
export const validateFileSizeForForm = (newFileUpload, maxFileSize = 307200) => {
  const { file } = newFileUpload || {};
  // No file = valid (optional attachment)
  if (!file) return true;
  // Compressible images skip size validation - compression will handle it
  if (isCompressibleImage(file)) return true;
  // Non-image files must be under maxFileSize
  return maxFileSize > 0 && file.size < maxFileSize;
};

export const validateFileType = (newFileUpload) => {
  try {
    const {
      file,
    } = newFileUpload || {};
    const {
      name,
      type,
    } = file || {};

    const types = [
      'image/png', 'image/jpg', 'image/jpeg', 'image/gif',
      'video/mp4', 'audio/mpeg', 'video/webm', 'video/x-matroska', 'video/3gpp', 'video/3gp', 'video/x-ms-wmv', 'video/x-msvideo',
      'video/x-flv', 'video/flv', 'video/quicktime', 'video/mov', 'video/avi', 'video/msvideo', 'video/wmv',
      'audio/ogg', 'audio/wav', 'audio/x-amr', 'audio/aac', 'audio/vnd.dlna.adts', 'audio/x-ms-wma', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/amr', 'audio/amr-wb',
      // CON-1674 — what MediaRecorder actually produces. The list already had
      // video/webm but not audio/webm, so a recorded memo was rejected as the
      // wrong type even though .webm was an accepted extension.
      'audio/webm',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/rtf', 'text/vcard', 'text/x-vcard',
      'application/zip', 'application/x-compressed', 'application/x-zip-compressed', 'application/x-rar-compressed', 'application/vnd.rar', 'application/x-7z-compressed', 'application/octet-stream'
    ];
    const fileExtension = [
      '.png', '.jpg', '.jpeg', '.gif', '.mp4', '.mp3', '.webm', '.ogg', '.wav',
      '.flv', '.mov', '.wmv', '.avi', '.mkv', '.3gp', '.flac', '.m4a', '.aac',
      '.pdf', '.doc', '.docx', '.txt', '.rtf', '.vcf', '.zip', '.rar', '.7z'
      // '.wma', '.amr'
    ];

    // MediaRecorder reports the codec as a parameter — 'audio/webm;codecs=opus'
    // — which no entry in the list carries, so compare on the base type only.
    // Nothing in `types` has a parameter, so this cannot loosen an existing match.
    const baseType = type ? `${type}`.split(';')[0].trim().toLowerCase() : '';
    const isCorrectFileExtension = name && fileExtension.find((extension) => `${name}`.toLowerCase().endsWith(extension));
    const isCorrectFiletype = baseType && types.find((item) => item === baseType);

    return !file || (isCorrectFileExtension && isCorrectFiletype);
  } catch (e) {
    return false;
  }
};

export const getUserContainerName = (databaseBeingUsed, $userId = 0) => {
  const { container } = STORAGE;
  return `${databaseBeingUsed || ''}${container}${$userId || 0}`;
};

export const isUserContainer = (databaseBeingUsed, userId, container) => {
  // If contaner = null, defaults to user container.
  if (!container) {
    return true;
  }
  const containerName = getUserContainerName(databaseBeingUsed, userId);
  return containerName === container;
};

export const getContainerNameAndFileNameFromFileURL = (imageFilePath) => {
  const fileOptions = imageFilePath.match(/(.*)\/(.*?)\/(.*?)$/);
  const fileName = fileOptions && !isEmpty(fileOptions[3]) ? fileOptions[3] : false;
  const containerName = fileOptions && !isEmpty(fileOptions[2]) ? fileOptions[2] : false;

  return containerName && fileName ? {
    containerName,
    fileName
  } : false;
};

export const uploadFile = async (file, newFileName, userId, account, sas, databaseBeingUsed, container) => {
  try {
    const containerName = container || getUserContainerName(databaseBeingUsed, userId);
    const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net${sas}`);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const isContainerExists = await containerClient.exists();
    if (!isContainerExists) {
      await containerClient.create();
    }
    const blockBlobClient = containerClient.getBlockBlobClient(newFileName);
    const contentType = file.type || 'application/octet-stream';
    return await blockBlobClient.upload(file, file.size, {
      blobHTTPHeaders: {
        blobContentType: contentType,
      },
    });
    // return await blockBlobClient.upload(file, file.size);
    // 2025-05-20 - We may want to add this back in the future.
    // const contentType = file.type || 'application/octet-stream';
    // const uploadResponse = await blockBlobClient.upload(file, file.size, {
    //   blobHTTPHeaders: {
    //     blobContentType: contentType,
    //   },
    // });

    // return uploadResponse;
  } catch (e) {
    return false;
  }
};

export const deleteFile = async (imageFilePath, account, sas) => {
  try {
    const { containerName, fileName } = getContainerNameAndFileNameFromFileURL(imageFilePath) || {};
    if (!containerName || !fileName) {
      return false;
    }
    const blobServiceClient = new BlobServiceClient(`https://${account}.blob.core.windows.net${sas}`);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(fileName);
    return await blockBlobClient.deleteIfExists(imageFilePath);
  } catch (e) {
    return false;
  }
};

export const addSasToUrl = (url, sas) => (`${url}${sas}`);

export const getImageFileURL = (name, userId, account, sas, databaseBeingUsed, container, addSas = false) => {
  if (isEmpty(name) || typeof name !== 'string') {
    return name;
  }
  const containerName = container || getUserContainerName(databaseBeingUsed, userId);
  const url = !name.match(/^http(s?):\/\//i) ? `https://${account}.blob.core.windows.net/${containerName}/${name}` : name;
  return (addSas && !url.includes('?')) ? addSasToUrl(url, sas) : url;
};

export const loadFileToAzureAndGetStatusCode = async (fileUpload, attachmentStr, userId, account, sas, databaseBeingUsed, container, fileWasNotUpload, fileWasNotDelete, doYouNeedFullUrl = false, uniqueFileSlug = false, allowDeleteFile = true, compressionMaxSizeBytes = null) => {
  // NOTE: Eventually we should allow here to delete a file any time.  But, it would need to call an endpoint on the backend to see if any other orders (or anything else) references the image first.
  const newAttachmentStr = !attachmentStr && attachmentStr !== false ? '' : attachmentStr;

  // Compress image if needed before upload (images only, skips PDFs/docs/videos)
  // compressionMaxSizeBytes: null = default 300KB, or pass a custom value in bytes
  let fileToUpload = fileUpload?.file;
  if (fileUpload && fileToUpload) {
    try {
      const compressionResult = await compressImageIfNeeded(fileToUpload, compressionMaxSizeBytes);
      if (!compressionResult.success) {
        // Compression failed - log and call the error callback
        console.error('Image compression failed in loadFileToAzureAndGetStatusCode:', compressionResult.error);
        fileWasNotUpload();
        return null;
      }
      // Use compressed file (or original if no compression was needed)
      fileToUpload = compressionResult.file;
    } catch (compressionError) {
      // Compression threw an unexpected error - log and continue with original file
      console.error('Unexpected error during image compression:', compressionError);
      // Fall through and try to upload the original file
    }
  }

  const newFileName = fileUpload ? createFileName(fileToUpload?.name || '.jpg', uniqueFileSlug) : '';
  if (fileUpload) {
    const wasFileUploaded = await uploadFile(fileToUpload, newFileName, userId, account, sas, databaseBeingUsed, container);
    if (!wasFileUploaded) {
      fileWasNotUpload();
      return null;
    }
  }
  const isFileDeleted = (fileUpload === null || fileUpload) && !isEmpty(attachmentStr);
  if (allowDeleteFile && isFileDeleted && isUserContainer(databaseBeingUsed, userId, container) && (!uniqueFileSlug || findUniqueFileSlug(attachmentStr, uniqueFileSlug))) {
    const wasFileDeleted = await deleteFile(getImageFileURL(newAttachmentStr, userId, account, sas, databaseBeingUsed, container), account, sas);
    if (!wasFileDeleted) {
      fileWasNotDelete();
      return null;
    }
  }
  const newValuesAttachment = isFileDeleted ? '' : newAttachmentStr;

  if (doYouNeedFullUrl) {
    return fileUpload ? getImageFileURL(newFileName, userId, account, sas, databaseBeingUsed, container) : newValuesAttachment;
  }

  return fileUpload ? newFileName : newValuesAttachment;
};

export const getDisplayFileName = (fileName) => {
  if (!fileName || typeof fileName !== 'string') {
    return fileName;
  }

  let cleanName = fileName.trim();

  // Remove full URL if present - extract just the filename
  if (cleanName.match(/^https?:\/\//i)) {
    const urlParts = cleanName.split('/');
    cleanName = urlParts[urlParts.length - 1];
  }

  // Remove EO-ID prefix pattern: EO-ID-YYYY-MM-DD-HH-MM-SS-MS-originalfilename.ext
  // Updated pattern to handle cases where milliseconds might be missing or replaced
  const eoIdPattern = /^EO-ID-\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-(?:\d{1,3}-)?(.+)$/;
  const eoIdMatch = cleanName.match(eoIdPattern);
  if (eoIdMatch) {
    const [, originalFileName] = eoIdMatch;
    cleanName = originalFileName;
  }

  // Only apply the timestamp pattern if we haven't already processed an EO-ID pattern
  if (!eoIdMatch) {
    const timestampPattern = /^[a-z0-9]{1,10}-(.+)$/;
    const timestampMatch = cleanName.match(timestampPattern);
    if (timestampMatch && timestampMatch[1].includes('.')) {
      const [, originalFileName] = timestampMatch;
      cleanName = originalFileName;
    }
  }

  return cleanName;
};
